import { BooleanField, DateTimeField, FormType, SelectField, TextField } from "@hex-insights/forms";
import { StudentEnrollmentIntentDeclineReason } from "../../../Utilities";
import { StudentEnrollmentIntentFormValues } from "../../FormUtilities";
import { BooleanSwitchInput } from "../_General";
import { AcademicYearSelectField } from "../AcademicYear";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";

/**
 * Generic props for fields of the StudentEnrollmentIntent model.
 */
type FieldProps<K extends keyof StudentEnrollmentIntentFormValues.Base = keyof StudentEnrollmentIntentFormValues.Base> =
	BaseFieldProps<Pick<StudentEnrollmentIntentFormValues.Base, K>>;

/**
 * Generic props for fields of the StudentEnrollmentIntent model that only appear in the detail form.
 */
type DetailFieldProps<
	K extends keyof StudentEnrollmentIntentFormValues.Detail = keyof StudentEnrollmentIntentFormValues.Detail,
> = BaseFieldProps<Pick<StudentEnrollmentIntentFormValues.Detail, K>>;

/**
 * Field component for the `createdAt` field of the StudentEnrollmentIntent model.
 */
export function CreatedAt({ formState, ...props }: DetailFieldProps<"createdAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="createdAt"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

/**
 * Field component for the `isEnrolling` field of the StudentEnrollmentIntent model.
 */
export function IsEnrolling({ formState, ...props }: FieldProps<"isEnrolling">) {
	return <BooleanField formState={formState} name="isEnrolling" Input={BooleanSwitchInput} {...props} />;
}

/**
 * Field component for the `isInterestedInMealPlan` field of the StudentEnrollmentIntent model.
 */
export function IsInterestedInMealPlan({ formState, ...props }: FieldProps<"isInterestedInMealPlan">) {
	return <BooleanField formState={formState} name="isInterestedInMealPlan" Input={BooleanSwitchInput} {...props} />;
}

// TODO hex-core-update enum value ordering in SRSL
const declineReasonOptions = [
	{ value: StudentEnrollmentIntentDeclineReason.Relocation },
	{ value: StudentEnrollmentIntentDeclineReason.Financial },
	{ value: StudentEnrollmentIntentDeclineReason.Academic },
	{ value: StudentEnrollmentIntentDeclineReason.Social },
	{ value: StudentEnrollmentIntentDeclineReason.Administrative },
	{ value: StudentEnrollmentIntentDeclineReason.Health },
	{ value: StudentEnrollmentIntentDeclineReason.Graduating },
	{ value: StudentEnrollmentIntentDeclineReason.Other },
];

/**
 * Field component for the `declineReason` field of the StudentEnrollmentIntent model.
 */
export function DeclineReason({ formState, ...props }: FieldProps<"declineReason">) {
	return (
		<SelectField
			formState={formState}
			name="declineReason"
			options={declineReasonOptions}
			blankValue={null}
			{...props}
		/>
	);
}

/**
 * Field component for the `declineReasonDescription` field of the StudentEnrollmentIntent model.
 */
export function DeclineReasonDescription({ formState, ...props }: FieldProps<"declineReasonDescription">) {
	return <TextField formState={formState} name="declineReasonDescription" multiLine {...props} />;
}

export namespace AcademicYear {
	export type SelectProps = FieldProps<"academicYearID"> &
		Omit<
			AcademicYearSelectField.SelectProps<StudentEnrollmentIntentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `academicYear` edge of the StudentEnrollmentIntent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <AcademicYearSelectField.Select formState={formState} name="academicYearID" {...props} />;
	}
}

export namespace Student {
	export type SelectProps = FieldProps<"studentID"> &
		Omit<StudentSelectField.SelectProps<StudentEnrollmentIntentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `student` edge of the StudentEnrollmentIntent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.Select formState={formState} name="studentID" {...props} />;
	}
}
